import {forwardRef} from '@chakra-ui/react';
import {mode} from '@chakra-ui/theme-tools';
import type {GroupBase, Props, SelectInstance} from 'chakra-react-select';
import {Select} from 'chakra-react-select';
import type {ReactElement, RefAttributes} from 'react';

import {components as darkComponents} from '../../design-tokens/dark';
import {components} from '../../design-tokens/light';
import {useAvatarSelectStyleConfig} from '../../theme-config';

import {selectComponents} from './components';
import type {SelectRef} from './types';

const lightColors = components.colors.components[`context-menu`];
const darkColors = darkComponents.colors.components[`context-menu`];

export type ReactSelectComponent = <
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: Props<Option, IsMulti, Group> & RefAttributes<SelectInstance<Option, IsMulti, Group>>,
) => ReactElement<any>;

export const ReactSelect = forwardRef(
  <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
    props: Props<Option, IsMulti, Group>,
    ref: SelectRef<Option, IsMulti, Group>,
  ) => {
    const chakraStyles = useAvatarSelectStyleConfig(props);
    const chakraStyleProp = props.chakraStyles;

    return (
      <Select
        size={`lg`}
        {...props}
        chakraStyles={{
          indicatorSeparator: (provided, _state) => ({...provided, ...chakraStyles.indicatorSeparator}),
          indicatorsContainer: (provided, _state) => ({...provided, ...chakraStyles.indicatorsContainer}),
          dropdownIndicator: (provided, _state) => ({...provided, ...chakraStyles.dropdownIndicator}),
          control: (provided, _state) => ({
            ...provided,
            ...chakraStyles.control,
            ...chakraStyleProp?.control?.(provided, _state),
          }),
          downChevron: (provided, _state) => ({...provided, ...chakraStyles.downChevron}),
          valueContainer: (provided, _state) => ({
            ...provided,
            ...chakraStyles?.valueContainer,
            ...chakraStyleProp?.valueContainer?.(provided, _state),
          }),
          menuList: (provided, _state) => ({
            ...provided,
            ...chakraStyles?.menuList,
            ...chakraStyleProp?.menuList?.(provided, _state),
          }),
          option: (provided, _state) => ({
            ...provided,
            ...chakraStyles.option,
            '&[aria-selected=true], &[data-selected]': {
              background: _state.isSelected
                ? mode(lightColors[`background-selected`], darkColors[`background-selected`])
                : `white`,
              color: mode(lightColors[`text-default`], darkColors[`text-default`]),
            },
            '&[aria-selected=true]:active, &[data-selected]:active': {
              background: mode(lightColors[`background-pressed`], darkColors[`background-pressed`]),
            },
            _hover: {
              background: _state.isSelected
                ? mode(lightColors[`background-selected`], darkColors[`background-selected`])
                : mode(lightColors[`background-hover`], darkColors[`background-hover`]),
            },
          }),
        }}
        ref={ref}
        components={{...selectComponents, ...props.components}}
      />
    );
  },
) as ReactSelectComponent;
